import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import bkstApi from "../../common/api";
import ConfigContext from "../../common/context/ConfigContext";
import currency from "currency.js";
import { areTipsEnabled, calculatePlatformFee, calculateSalesTax } from "../../common/util";
import moment from "moment";
import StripePaymentElement from "../component/StripePaymentElement";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useTheme } from "@mui/material/styles";
import OrderPaymentSummaryV2 from "../component/OrderPaymentSummaryV2";
import PhoneField from "../../common/component/PhoneField";
import { Link as RouterLink } from "react-router-dom";
import { AppStateContext } from "../../common/context/AppStateContext";
import { useNavigate } from "react-router";
import LineItemV3 from "../../common/component/LineItemV3";
import FulfillmentOptionsV2 from "../../cart/component/FulfillmentOptionsV2";
import BlockingError from "../../common/component/BlockingError";
import EmailInput from "../../common/component/EmailInput";
import XplorPaymentElement from "../component/XplorPaymentElement";
import QuoteChat from "../component/QuoteChat";

const key = process.env.REACT_APP_STRIPE_KEY;
const promise = loadStripe(key);

const TIP_CHOICES = [
  { value: "0", title: "No Tip" },
  { value: "5", title: "5%" },
  { value: "10", title: "10%" },
  { value: "15", title: "15%" },
  { value: "20", title: "20%" },
  { value: "25", title: "25%" },
];

export default function CartV2(props) {
  const siteConfig = useContext(ConfigContext);
  const navigate = useNavigate();

  const { setCartState } = useContext(AppStateContext);

  const theme = useTheme();
  const ref = useRef(null);

  const { cartId } = useParams();

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");

  const [cart, setCart] = useState("");
  const [options, setOptions] = useState("");
  const [payment, setPayment] = useState("");

  const [fulfillment, setFulfillment] = useState({ shipping: "pickup" });

  const [tipRate, setTipRate] = useState("0");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [paymentDueType, setPaymentDueType] = useState("full");

  const [error, setError] = useState("");
  const [blockingError, setBlockingError] = useState("");

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [deliveryPayBlocked, setDeliveryPayBlocked] = useState(false);
  const handleDeliveryPayBlockedChange = useCallback((blocked) => {
    setDeliveryPayBlocked(blocked);
  }, []);
  const readyToSync = useRef(false);

  const stripeOptions = {
    clientSecret,
    appearance: {
      labels: "floating",
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorDanger: "#df1b41",
        borderRadius: "4px",
      },
    },
  };

  useEffect(() => {
    window.scrollTo(0, 50);
    readyToSync.current = false;
    setProcessing(true);

    bkstApi(`/site/${siteConfig.id}/cartV2/${cartId}`).then((res) => {
      setClientSecret(res.data.cart.clientSecret || "");
      setPaymentIntentId(res.data.cart.paymentIntentId || "");

      // Drop live third-party delivery quote on load — customer must re-check availability.
      // Keep in-house price/zone; those are the baker's quoted fee, not a DSP estimate.
      if (res.data.cart.options?.shipping?.delivery) {
        const delivery = res.data.cart.options.shipping.delivery;
        if (delivery.type === "third-party") {
          const { quote, price, ...deliveryWithoutQuote } = delivery;
          res.data.cart.options = {
            ...res.data.cart.options,
            shipping: {
              ...res.data.cart.options.shipping,
              delivery: deliveryWithoutQuote,
            },
          };
        }
        if (res.data.cart.payment) {
          res.data.cart.payment = {
            ...res.data.cart.payment,
            delivery: 0,
          };
        }
      }

      setCart(res.data.cart);

      setPayment(res.data.cart.payment);
      setOptions(res.data.cart.options);

      // Restore fulfillment details on load, but not delivery quote (must re-check)
      const shippingType = res.data.cart.shipping?.type || "pickup";
      const deliveryOpt = res.data.cart.options?.shipping?.delivery;
      const savedLocation = res.data.cart.shipping?.location;
      let location;
      if (shippingType === "delivery") {
        location = {
          ...(savedLocation?.street ? savedLocation : {}),
          timeslots: deliveryOpt?.timeslots || savedLocation?.timeslots,
          rules: deliveryOpt?.rules || savedLocation?.rules,
        };
      } else {
        location = savedLocation || res.data.cart.options.shipping.pickup.locations[0];
      }

      setFulfillment({
        shipping: shippingType,
        location,
        date: res.data.cart.shipping.date || sessionStorage.getItem("cart.date"),
        time: res.data.cart.shipping.time || "",
        provider: deliveryOpt?.provider,
      });

      setTipRate(res.data.cart.tipRate || "0");
      setPaymentDueType(
        res.data.cart.paymentDueType ||
          (+res.data.cart.payment.due < +res.data.cart.payment.total ? "deposit" : "full")
      );

      if (res.data.cart.customer) {
        setFirstName(res.data.cart.customer.firstName || "");
        setLastName(res.data.cart.customer.lastName || "");
        setEmail(res.data.cart.customer.email || "");
        setPhone(res.data.cart.customer.phone || "");
      }

      setLoading(false);
      setProcessing(false);

      const itemCount = res.data.cart.items.reduce((acc, o) => +acc + +o.quantity, 0);
      setCartState(cartId, itemCount);

      setTimeout(() => {
        readyToSync.current = true;
      }, 0);
    });
  }, [cartId]);

  useEffect(() => {
    if (cart) {
      setPayment(recalculatePayment());

      // reset based on date
      if (paymentDueType === "deposit" && !isDepositAllowed()) {
        setPaymentDueType("full");
      }
    }
  }, [fulfillment, tipRate, paymentDueType]);

  // Merge live fulfillment/customer into cart so PUTs don't overwrite server with stale local cart.
  const buildCartPayload = (overrides = {}) => {
    const updatedCart = { ...cart, tipRate, paymentDueType, ...overrides };

    updatedCart.shipping = {
      ...cart.shipping,
      type: fulfillment.shipping,
      location: fulfillment.location,
      date: fulfillment.date || "",
      time: fulfillment.time || "",
    };

    if (fulfillment.shipping === "delivery") {
      const existingDelivery = cart.options?.shipping?.delivery || {};
      const isThirdParty = existingDelivery.type === "third-party";

      if (isThirdParty) {
        const { quote, price, ...deliveryWithoutQuote } = existingDelivery;
        updatedCart.options = {
          ...cart.options,
          shipping: {
            ...cart.options.shipping,
            delivery: {
              ...deliveryWithoutQuote,
              type: "third-party",
              provider: fulfillment.provider || deliveryWithoutQuote.provider,
              location: fulfillment.location,
              date: fulfillment.date || "",
              time: fulfillment.time || "",
              ...(fulfillment.quote != null ? { quote: fulfillment.quote } : {}),
            },
          },
        };
      } else {
        // in-house: keep quoted price + zone location; customer address lives on shipping.location
        updatedCart.options = {
          ...cart.options,
          shipping: {
            ...cart.options.shipping,
            delivery: {
              ...existingDelivery,
              type: existingDelivery.type || "in-house",
              price: existingDelivery.price,
              location: existingDelivery.location,
              date: fulfillment.date || "",
              time: fulfillment.time || "",
              ...(fulfillment.provider ? { provider: fulfillment.provider } : {}),
              ...(fulfillment.quote != null ? { quote: fulfillment.quote } : {}),
            },
          },
        };
      }
    }

    if (!cart.reqId) {
      updatedCart.customer = { firstName, lastName, email, phone };
    }

    return updatedCart;
  };

  // Sync tip / fulfillment / paymentDue / customer to DB.
  // When delivery has a live quote, send it on PUT so the server can persist + revalidate.
  useEffect(() => {
    if (!readyToSync.current || !cart) {
      return;
    }

    const timer = setTimeout(() => {
      // Persist only — keep payment/cart UI frontend-owned (except remove item)
      updateCart(buildCartPayload(), { applyResponse: false }).catch(() => {});
    }, 700);

    return () => clearTimeout(timer);
  }, [fulfillment, tipRate, paymentDueType, firstName, lastName, email, phone]);

  const isDepositAllowed = () => {
    return fulfillment.date && moment(fulfillment.date).diff(moment(), "days") >= 7;
  };

  const calculatePaymentForTipRate = (rate) => {
    const subtotal = currency(cart.items.reduce((acc, o) => +acc + (+o.total || +o.price), 0));
    const discount = currency(cart.payment.discount);
    const discountedSubtotal = subtotal.subtract(discount);
    const delivery = currency((fulfillment.shipping === "delivery" && fulfillment.quote) || 0);
    const tipAmount = discountedSubtotal
      .add(delivery)
      .multiply(+rate || 0)
      .divide(100);

    let platformFee = currency(0);
    if (cart.adjustments && cart.adjustments.platformFee) {
      platformFee = calculatePlatformFee(discountedSubtotal, cart.adjustments.platformFee);
    }

    const salesTax = calculateSalesTax(cart.items, {
      subtotal: subtotal.value,
      discount: discount.value,
      delivery: delivery.value,
      platformFee: platformFee.value,
      salesTaxConfig: cart.adjustments?.salesTax,
      collectSalesTax: cart.settings?.collectSalesTax,
    });

    const total = discountedSubtotal.add(delivery).add(tipAmount).add(platformFee).add(salesTax);

    let due = total;
    if (paymentDueType === "deposit" && isDepositAllowed()) {
      due = total.multiply(0.5);
    }

    return {
      subtotal: subtotal.value,
      discount: discount.value,
      delivery: delivery.value,
      tip: tipAmount.value,
      platformFee: platformFee.value,
      salesTax: salesTax.value,
      total: total.value,
      due: due.value,
    };
  };

  const recalculatePayment = () => calculatePaymentForTipRate(tipRate);

  const pickupNeedsLocationChoice =
    fulfillment.shipping === "pickup" && (options?.shipping?.pickup?.locations?.length ?? 0) > 1;
  const hasPickupLocationSelected = (options?.shipping?.pickup?.locations || []).some((l) =>
    fulfillment?.location?.id
      ? l.id === fulfillment.location.id
      : Boolean(fulfillment?.location?.street) && l.street === fulfillment.location.street
  );
  const disablePayForPickupLocation = pickupNeedsLocationChoice && !hasPickupLocationSelected;

  const validateFields = () => {
    if (fulfillment.shipping === "delivery" && deliveryPayBlocked) {
      setError(
        "Complete delivery: choose date and time, enter your address, run Check availability successfully, then Add delivery before paying."
      );
      return false;
    }

    let errors = "";
    if (pickupNeedsLocationChoice && !hasPickupLocationSelected) {
      errors += "Please select a pickup location. ";
    }
    if (!fulfillment.date) {
      errors += "Date is missing. ";
    }
    if (!fulfillment.time) {
      errors += "Time is missing. ";
    }
    if (!cart.reqId) {
      if (!firstName) {
        errors += "First Name is missing. ";
      }
      if (!lastName) {
        errors += "Last Name is missing. ";
      }
      if (!phone) {
        errors += "Phone is missing. ";
      } else if (phone.length < 10) {
        errors += "Invalid Phone Number. ";
      }

      if (!email) {
        errors += "Email is missing. ";
      } else if (email.length < 5 || !email.includes("@") || !email.includes(".")) {
        errors += "Invalid Email. ";
      }
    }

    if (errors) {
      setError(errors);
      return false;
    }
    return true;
  };

  const validate = async () => {
    if (!validateFields()) {
      return false;
    }

    setProcessing(true);

    return bkstApi
      .post(`/site/${siteConfig.id}/cartV2/${cartId}/verify`, {
        paymentIntentId,
        shipping: {
          type: fulfillment.shipping,
          location: fulfillment.location,
          date: fulfillment.date,
          time: fulfillment.time,
        },
        tipRate,
        due: payment.due,
        paymentDueType,
        customer: cart?.reqId ? cart.customer : { firstName, lastName, email, phone },
        itemUpdTs: cart.itemUpdTs,
        salesTax: payment.salesTax,
      })
      .then((res) => {
        setCart(res.data.cart);
        setPayment(res.data.cart.payment);
        setProcessing(false);
        return true;
      })
      .catch((error) => {
        if (error?.response?.data?.cart) {
          setCart(error.response.data.cart);
          setPayment(error.response.data.cart.payment);

          setError(error.response.data.msg);
        } else {
          setBlockingError(error.response.data.msg);
        }
        setProcessing(false);
        return false;
      });
  };

  const chargePaymentMethod = (paymentMethodId) => {
    const payload = {
      paymentIntentId,
      paymentMethodId,
    };

    bkstApi
      .post(`/site/${siteConfig.id}/moto`, payload)
      .then((res) => {
        navigate(`/payment-status?cart_id=${cartId}&payment_intent_client_secret=${clientSecret}`);
      })
      .catch((error) => {
        if (error?.response?.data) {
          setError(error.response.data.msg);
        } else {
          setError("Payment Declined. Please close this window and try again.");
        }
        setProcessing(false);

        return false;
      });
  };

  const updateCart = async (updatedCart, { applyResponse = false } = {}) => {
    return bkstApi
      .put(`/site/${siteConfig.id}/cartV2/${cartId}`, {
        paymentIntentId,
        paymentDueType,
        cart: updatedCart,
      })
      .then((res) => {
        if (applyResponse) {
          setCart(res.data.cart);
          setPayment(res.data.cart.payment);
          setOptions(res.data.cart.options);
        }
        if (res.data.deliveryQuoteInvalidated) {
          const delivery = res.data.cart.options?.shipping?.delivery;
          setFulfillment((prev) => {
            const nextLocation = delivery?.location || prev.location;
            const timeslots =
              nextLocation?.timeslots || delivery?.timeslots || prev.location?.timeslots;
            const rules = nextLocation?.rules || delivery?.rules || prev.location?.rules;
            return {
              ...prev,
              shipping: "delivery",
              location: {
                ...nextLocation,
                ...(timeslots ? { timeslots } : {}),
                ...(rules ? { rules } : {}),
              },
              date: delivery?.date || prev.date || "",
              time: delivery?.time || prev.time || "",
              provider: delivery?.provider || prev.provider,
              quote: undefined,
            };
          });
          if (applyResponse) {
            setOptions(res.data.cart.options);
          } else if (delivery) {
            setOptions((prev) => ({
              ...prev,
              shipping: {
                ...prev.shipping,
                delivery: {
                  ...delivery,
                  timeslots: delivery.timeslots || prev.shipping?.delivery?.timeslots,
                  rules: delivery.rules || prev.shipping?.delivery?.rules,
                },
              },
            }));
          }
          setError("Delivery price has changed. Please check delivery availability again.");
          return null;
        }
        return res.data.cart;
      })
      .catch(() => {
        setError("Unexpected error. Please refresh this page and try again");
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const removeItem = async (idx) => {
    setProcessing(true);

    const items = [...cart.items];
    items.splice(idx, 1);

    try {
      const updated = await updateCart(buildCartPayload({ items }), { applyResponse: true });
      if (updated) {
        const itemCount = updated.items.reduce((acc, o) => +acc + +o.quantity, 0);
        setCartState(cartId, itemCount);
      }
    } finally {
      setProcessing(false);
    }
  };

  const adjustQuantity = async (idx, change) => {
    const currentQty = +cart.items[idx]?.quantity || 1;
    const nextQty = currentQty + change;
    if (nextQty < 1) {
      return;
    }

    setProcessing(true);
    const items = cart.items.map((item, i) => {
      if (i !== idx) {
        return item;
      }
      // Line totals are stored as variantPrice * qty; scale with quantity so payment stays correct
      const unit = currency(+item.total || +item.price || 0).divide(currentQty);
      return {
        ...item,
        quantity: nextQty,
        total: unit.multiply(nextQty).value,
      };
    });

    try {
      const updated = await updateCart(buildCartPayload({ items }), { applyResponse: true });
      if (updated) {
        const itemCount = updated.items.reduce((acc, o) => +acc + +o.quantity, 0);
        setCartState(cartId, itemCount);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress ref={ref} />{" "}
        {processing && (
          <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </Box>
    );
  } else {
    const allowDeposit = isDepositAllowed();

    if (cart.items && cart.items.length > 0) {
      return (
        <Container maxWidth="md" sx={{ paddingTop: "2rem", paddingBottom: "10rem" }}>
          <Box mb={3} ref={ref}>
            <Typography variant="h1">Checkout</Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Choose options below & pay to confirm your order
            </Typography>
          </Box>
          <Typography component="h2" variant="overline" color="textSecondary">
            ITEMS
          </Typography>
          <Paper elevation={0}>
            <Box px={1.5} py={1}>
              {cart.items.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    py: 2,
                    borderBottom: idx < cart.items.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <LineItemV3
                    item={item}
                    {...(cart.type === "shop"
                      ? {
                          onDecrease: () => adjustQuantity(idx, -1),
                          onIncrease: () => adjustQuantity(idx, 1),
                          onRemove: () => removeItem(idx),
                        }
                      : {})}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
          <Box my={4}>
            <FulfillmentOptionsV2
              cartId={cartId}
              items={cart.items}
              options={options}
              fulfillment={fulfillment}
              onChange={(o) => {
                setFulfillment(o);
              }}
              defaultDate={cart.shipping?.date || ""}
              onDeliveryPayBlockedChange={handleDeliveryPayBlockedChange}
              disableDate={!!cart?.reqId}
            />
          </Box>
          {!!cart?.reqId && (
            <Box sx={{ mt: -2, mb: 4, display: "flex", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                Want different date?
              </Typography>
              <Typography variant="body2" component="div">
                <QuoteChat
                  reqId={cart?.reqId || ""}
                  baker={{ id: siteConfig.id }}
                  name={siteConfig.biz}
                  chat={[]}
                  viewReturnLabel="VIEW CART"
                  renderTrigger={(open) => (
                    <Link
                      component="button"
                      type="button"
                      underline="always"
                      onClick={(e) => {
                        e.preventDefault();
                        open();
                      }}
                      sx={{
                        verticalAlign: "baseline",
                        p: 0,
                        m: 0,
                        minWidth: 0,
                        font: "inherit",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Send Us a Message
                    </Link>
                  )}
                />
              </Typography>
            </Box>
          )}
          {areTipsEnabled(siteConfig.id) && (
            <Box my={4}>
              <RadioGroup
                value={tipRate}
                name="tipRate"
                onChange={(e) => setTipRate(e.target.value)}
                sx={{
                  display: "grid",
                  width: "100%",
                  gap: { xs: 1, sm: 1.5 },
                  gridTemplateColumns: {
                    xs: "repeat(3, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(6, minmax(0, 1fr))",
                  },
                }}
              >
                {TIP_CHOICES.map((opt) => {
                  const selected = tipRate === opt.value;
                  return (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            p: { xs: 0.5, sm: 0.5 },
                            flexShrink: 0,
                            alignSelf: "flex-start",
                            mt: { xs: 0.25, sm: 0.5 },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            pr: { xs: 1, sm: 1.25 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            component="div"
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{
                              fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
                              lineHeight: 1.25,
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {opt.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={400}
                            sx={{
                              display: "block",
                              mt: 0.25,
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.5625rem", sm: "0.625rem", md: "0.6875rem" },
                              lineHeight: 1.15,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {currency(calculatePaymentForTipRate(opt.value).tip).format()}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        mx: 0,
                        my: 0,
                        ml: 0,
                        mr: 0,
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        display: "flex",
                        flexDirection: "row-reverse",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        boxSizing: "border-box",
                        alignSelf: "stretch",
                        height: "100%",
                        minHeight: { xs: 52, sm: 54 },
                        border: "2px solid",
                        borderColor: selected ? "primary.main" : theme.palette.divider,
                        borderRadius: 2,
                        px: { xs: 1.25, sm: 1.5, md: 1.75 },
                        py: { xs: 1, sm: 1.125, md: 1.25 },
                        columnGap: { xs: 1.25, sm: 1.5, md: 2 },
                        bgcolor: "transparent",
                        cursor: "pointer",
                        transition: theme.transitions.create(["border-color"], {
                          duration: theme.transitions.duration.shorter,
                        }),
                        "& .MuiFormControlLabel-label": {
                          width: "100%",
                          minWidth: 0,
                          margin: 0,
                        },
                        "& .MuiButtonBase-root": {
                          margin: 0,
                          padding: { xs: "4px", sm: "6px" },
                        },
                      }}
                    />
                  );
                })}
              </RadioGroup>
            </Box>
          )}
          {!cart?.reqId && (
            <Box my={4}>
              <Typography component="h2" variant="overline" color="textSecondary">
                CONTACT DETAILS
              </Typography>
              <Paper elevation={0}>
                <Box px={2} pt={3} pb={2}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" fullWidth>
                        <TextField
                          value={firstName}
                          variant="outlined"
                          fullWidth
                          id="firstName"
                          label="First Name"
                          name="firstName"
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" fullWidth>
                        <TextField
                          value={lastName}
                          variant="outlined"
                          fullWidth
                          id="lastName"
                          label="Last Name"
                          name="lastName"
                          onChange={(e) => setLastName(e.target.value)}
                          required
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <EmailInput value={email} onChange={(val) => setEmail(val)} required={true} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" fullWidth>
                        <PhoneField
                          name="Phone"
                          value={phone}
                          onChange={(phone) => setPhone(phone)}
                          required
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Box>
          )}
          <Box my={4}>
            <Typography component="h2" variant="overline" color="textSecondary">
              PAYMENT SUMMARY
            </Typography>
            <Paper elevation={0}>
              <Box px={2} py={2}>
                <Box mb={2}>
                  <RadioGroup
                    row
                    value={paymentDueType}
                    name="paymentDueType"
                    onChange={(e) => {
                      setPaymentDueType(e.target.value);
                    }}
                  >
                    <FormControlLabel value="full" control={<Radio />} label="Pay in Full" />
                    <FormControlLabel
                      value="deposit"
                      control={<Radio />}
                      label={
                        allowDeposit
                          ? "Pay Deposit"
                          : "Pay Deposit (only for orders due in more than 7 days)"
                      }
                      disabled={!allowDeposit}
                    />
                  </RadioGroup>
                </Box>
                <OrderPaymentSummaryV2
                  subtotal={payment.subtotal}
                  discount={payment.discount}
                  delivery={payment.delivery}
                  tip={payment.tip}
                  platformFee={payment.platformFee}
                  salesTax={payment.salesTax}
                  total={payment.total}
                  due={payment.due}
                  date={moment(fulfillment.date).subtract(7, "days").format("MMM DD")}
                />
              </Box>
            </Paper>
          </Box>
          <Box my={4}>
            <Typography component="h2" variant="overline" color="textSecondary" gutterBottom>
              Pay with
            </Typography>
            <Paper elevation={0}>
              <Box px={2} pt={3} pb={4}>
                {error && (
                  <Box mb={2}>
                    <Alert severity="error" onClose={() => setError("")}>
                      {error}
                    </Alert>
                  </Box>
                )}
                {paymentIntentId ? (
                  <Elements stripe={promise} options={stripeOptions}>
                    <StripePaymentElement
                      amount={currency(payment.due).format()}
                      disabled={loading || deliveryPayBlocked || disablePayForPickupLocation}
                      validateFields={validateFields}
                      validate={validate}
                      returnUrl={`${window.location.origin}/payment-status?cart_id=${cartId}`}
                      showPolicy={true}
                    />
                  </Elements>
                ) : (
                  <XplorPaymentElement
                    amount={currency(payment.due).format()}
                    disabled={loading || deliveryPayBlocked || disablePayForPickupLocation}
                    validate={validate}
                    showPolicy={true}
                    publicKey={cart.xplor.publicKey}
                    onTokenSuccess={(mobileJwt, zip) => {
                      setProcessing(true);
                      bkstApi
                        .post(`/site/${siteConfig.id}/cart/${cartId}/payment`, {
                          mobileJwt,
                          amount: payment.due,
                          description: `Online Order for ${fulfillment?.date || ""}`,
                          zip,
                        })
                        .then((res) => {
                          const orderId = res.data?.orderId || "";
                          if (orderId) {
                            navigate(`/order/${orderId}`);
                            return;
                          }
                        })
                        .catch((err) => {
                          setError(err?.response?.data?.msg || "Payment failed. Please try again.");
                          setProcessing(false);
                        });
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Box>
          {processing && (
            <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          {blockingError && (
            <BlockingError
              title="Error!"
              cta="Clear Cart"
              onClick={() => {
                setCart("");
                navigate(-1);
              }}
            >
              <Box>
                <Typography>{blockingError}</Typography>
              </Box>
            </BlockingError>
          )}
        </Container>
      );
    } else {
      return (
        <Box my={6} textAlign={"center"}>
          <Box mb={2}>
            <Typography variant="h2" gutterBottom>
              Your cart is empty!
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={RouterLink}
            to="/shop"
          >
            Shop Now
          </Button>
        </Box>
      );
    }
  }
}
